(function () {
  'use strict';

  const gsap = window.gsap;
  const { easePower2, subEvents, PUB_SUB_EVENTS, QuantityInput } = window.Universe;

  class StickyAddtoCart {
    constructor() {
      this.DOM = undefined;
      this.cardTimeline = null;
      this.quantity = null;
      this.quantityInput = null;
      this.quantityUpdateUnsubscriber = null;
      this.intersectionObserver = null;
      this.scopeFrom = null;
      this.scopeTo = null;
      this.scopeFromVisibile = false;
      this.scopeToReached = false;
      this.formId = null;

      this.updateInstance = (variantData, quantity) => {
        if (!this.DOM) return;
        if (variantData) {
          this.handleOptionValueChange(variantData);
        }
        if (quantity !== undefined) {
          this.handleQuantityUpdate(quantity);
        }
      };

      this.onQuantityUpdate = (ref) => {
        const data = ref.data;
        this.quantity = data.quantity;

        if (this.quantity !== this.quantityInput.getQuantity()) {
          this.quantityInput.DOM.input.value = data.quantity;
        }
      };

      this.getProductForm = () => {
        return document.forms[this.formId];
      };

      this.handleIntersection = (entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.scopeFrom) {
            this.scopeFromVisibile = entry.boundingClientRect.bottom > 0;
          }
          if (entry.target === this.scopeTo) {
            this.scopeToReached = entry.isIntersecting;
          }
        });

        const card = this.DOM.wrap.firstElementChild;

        if (!this.scopeFromVisibile && !this.scopeToReached) {
          this.showCard(card);
        } else {
          this.hideCard(card);
        }
      };

      this.handleOptionValueChange = (variantData) => {
        if (variantData) {
          this.updateProductMedia(variantData);
          this.updateProductOptions(variantData);
        }
      };

      this.handleQuantityUpdate = (quantity) => {
        if (quantity !== undefined) {
          this.updateQuantityDisplay(quantity);
        }
      };

      this.onVariantClick = (event) => {
        this.handleVariantSelect(event.target);
      };

      this.handleVariantSelect = (variant) => {
        this.DOM.variantOptions.forEach((opt) => {
          opt.classList.remove('is-selected');
          if (opt.value == variant.value) {
            opt.classList.add('is-selected');
          }
        });

        subEvents.publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            target: variant,
          },
        });
      };

      this.init();
    }

    init(container = document) {
      const wrapper = container.querySelector('.js-product-sticky-form');
      if (!wrapper) return;

      this.DOM = {};
      this.DOM.wrap = wrapper;
      this.DOM.submitButton = this.DOM.wrap.querySelector('[type="submit"]');
      this.DOM.productOptions = this.DOM.wrap.querySelector('[data-sticky-product-options]');
      this.DOM.variantSelector = this.DOM.wrap.querySelector('.js-sticky-variant-selector');
      if (this.DOM.variantSelector) {
        this.DOM.variantOptions = this.DOM.variantSelector.querySelectorAll('.js-sticky-variant-option');
        this.DOM.variantSelector.addEventListener('change', this.onVariantClick);
      }
      this.DOM.productMedia = this.DOM.wrap.querySelector('[data-sticky-product-media]');
      this.DOM.errorMessage = this.DOM.wrap.querySelector('.product-form__error-message');
      this.DOM.stickySentinel = document.querySelector('#sticky-sentinel');
      this.DOM.stickySentinel.style.display = 'block';

      this.formId = this.DOM.wrap.getAttribute('data-form-id');
      this.scopeFrom = document.getElementById(this.formId);
      this.scopeTo = this.DOM.stickySentinel;

      if (!this.scopeFrom || !this.scopeTo) return;

      this.intersectionObserver = new IntersectionObserver(this.handleIntersection);
      this.intersectionObserver.observe(this.scopeFrom);
      this.intersectionObserver.observe(this.scopeTo);

      this.DOM.quantityInput = this.DOM.wrap.querySelector('.js-sticky-quantity-selector');

      if (this.DOM.quantityInput) {
        this.quantityInput = new QuantityInput(this.DOM.quantityInput);
        this.quantity = this.quantityInput.getQuantity();
        this.quantityUpdateUnsubscriber = subEvents.subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityUpdate);
      }
    }

    destroy() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      if (this.DOM && this.DOM.variantSelector) {
        this.DOM.variantSelector.removeEventListener('change', this.onVariantClick);
      }
      if (this.DOM && this.DOM.stickySentinel) {
        this.DOM.stickySentinel.style.display = 'none';
      }
      this.DOM = undefined;
    }

    showCard(card) {
      if (!card) return;
      if (this.cardTimeline) this.cardTimeline.kill();
      this.cardTimeline = gsap
        .timeline()
        .set(card, { visibility: 'visible' })
        .to(card, { opacity: 1, y: 0, duration: 0.2, ease: easePower2 });
    }

    hideCard(card) {
      if (!card) return;
      if (this.cardTimeline) this.cardTimeline.kill();
      this.cardTimeline = gsap
        .timeline()
        .to(card, { opacity: 0, y: 15, duration: 0.2, ease: easePower2 })
        .set(card, { visibility: 'hidden' });
    }

    updateProductMedia(currentVariant) {
      if (!currentVariant || !currentVariant.featured_media) return;

      const image = this.DOM.productMedia ? this.DOM.productMedia.querySelector('img') : null;
      if (!image) return;

      const newImage = new Image(
        currentVariant.featured_media.preview_image.width,
        currentVariant.featured_media.preview_image.height
      );

      newImage.alt = currentVariant.featured_media.alt;
      newImage.src = currentVariant.featured_media.preview_image.src;
      newImage.srcset = this.generateSrcset(currentVariant.featured_media.preview_image);
      newImage.sizes = image.sizes;

      this.DOM.productMedia.replaceChildren(newImage);
    }

    updateProductOptions(currentVariant) {
      if (!currentVariant || !this.DOM.productOptions) return;
      this.DOM.productOptions.innerText = currentVariant.title;
    }

    updateQuantityDisplay(quantity) {
      if (this.DOM.quantityInput && this.DOM.quantityInput.value !== quantity.toString()) {
        this.DOM.quantityInput.value = quantity;
        this.DOM.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    handleErrorMessage(errorMessage = false) {
      const productForm = this.getProductForm();
      if (productForm && productForm.hideErrors) return;

      this.DOM.errorMessage = this.DOM.errorMessage || this.DOM.wrap.querySelector('.product-form__error-message');
      if (!this.DOM.errorMessage) return;

      this.DOM.errorMessage.toggleAttribute('hidden', !errorMessage);
      this.DOM.errorMessage.innerText = errorMessage;
    }

    toggleSubmitButton(disable = true, text) {
      if (!this.DOM.submitButton) return;

      const submitButtonText = this.DOM.submitButton.querySelector('.btn-text');
      const submitButtonTextChild = this.DOM.submitButton.querySelector('.btn-text span');

      if (disable) {
        this.DOM.submitButton.setAttribute('disabled', '');
        if (text) {
          (submitButtonTextChild || submitButtonText).textContent = text;
        }
      } else {
        this.DOM.submitButton.removeAttribute('disabled');
        (submitButtonTextChild || submitButtonText).textContent =
          this.DOM.submitButton.getAttribute('data-pre-order') === 'true'
            ? theme.variantStrings.preOrder
            : theme.variantStrings.addToCart;
      }
    }

    generateSrcset(image) {
      const widthsAttr = this.DOM.productMedia ? this.DOM.productMedia.getAttribute('data-widths') : null;
      const widths = widthsAttr
        ? widthsAttr.split(',').map((width) => parseInt(width))
        : null;
      if (!widths) return '';
      return widths
        .filter((width) => width <= image.width)
        .map((width) => image.src + '&width=' + width + ' ' + width + 'w')
        .join(', ');
    }
  }

  window.Universe.StickyAddtoCart = StickyAddtoCart;
})();
